import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { actionToneOf, factToneOf, type BoardMatter, type CoastalDecision, type LimitsView, type PoliceView, type VendorTone } from '@claims/core';
import { api } from './api.js';
import { FACT_BUTTON, InlineFact, useMe } from './facts.js';
import { Glyph } from './icons.js';
import { PressButton } from './press-button.js';
import { usePress } from './press.js';

// THE CASE VIEW'S POLICE REPORT AND POLICY LIMITS AREAS (Rod 2026-10-21: "i would like to incorporate both of those api tools into claims
// console"; the three-views design, section 4: a status chip on the case, never a nav page). One card under the case's records, one line per
// area: its name, the chip, one plain sentence (core vendors.ts), and the one action that state allows:
//   Police report: "Order paid report" (it read "Order report" until the action tones, 2026-10-25) only when the controller's latest decision
//     run says a press orders now and the console's POLICE_ORDER and write-back are on. One paid order to Clymer & Beverly per crash, under
//     the main client's case; the controller decides it again live.
//   Policy limits: Approve paid search / Not now only on a search the controller suggested (Neila: never a free request button; Rod 10-09:
//     every order needs a claims person's approval). Status only, never a dollar figure (decision B, 2026-10-13).
// No confirm step (Rod 10-08): the press is the decision. Both orders are paid, so both presses are red (core action_tone.ts).

export const TONE_CHIP: Readonly<Record<VendorTone, { cls: string; icon: string }>> = {
  need: { cls: 'stc-need', icon: 'alert' }, working: { cls: 'stc-working', icon: 'hourglass' }, done: { cls: 'stc-opened', icon: 'win' },
  bad: { cls: 'stc-need', icon: 'alert' }, quiet: { cls: 'stc-none', icon: 'minus' },
};
export function VendorChip({ chip, tone }: { chip: string; tone: VendorTone }) {
  const c = TONE_CHIP[tone];
  return <span className={`stc ${c.cls}`} data-tone={tone}><Glyph name={c.icon} /><span>{chip}</span></span>;
}

const REFUSED: Record<string, string> = {
  write_back_off: 'Entering facts is not switched on yet.', police_order_off: 'Ordering police reports from Sweet Claims is not switched on yet.',
  coastal_off: 'Policy-limits searches are not switched on in Sweet Claims yet.', no_role: 'Your login cannot do this.',
  already_decided: 'Someone already decided this one.', not_found: 'This suggestion is gone; refresh the page.',
  rate_limit: 'You entered 60 facts in the last hour; wait a few minutes.', police_order_def_only: 'A police report is ordered on the at-fault side only.',
};
const refusedWords = (code: string | undefined) => `Not saved: ${REFUSED[code ?? ''] ?? String(code ?? 'unknown').replace(/_/g, ' ')}`;

function OrderReport({ m }: { m: BoardMatter }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // the first at-fault record with no claim, else the case's missing-DEF row (the controller orders for the crash either way)
  const def = m.rows.find((r) => r.side === 'DEF' && !r.gap && !r.claim) ?? null;
  const press = async () => {
    setMsg(null);
    try {
      const res = await api.fact({ matterId: m.id, insuranceId: def ? def.insuranceId : null, side: 'DEF', kind: 'police_order', value: null, carrierAccountId: null, basis: null });
      if (res.result === 'saved') {
        setMsg({ ok: true, text: 'Saved. The system orders the police report within 15 minutes, once for this crash.' });
        void qc.invalidateQueries({ queryKey: ['matter', m.id] }); void qc.invalidateQueries({ queryKey: ['board'] }); void qc.invalidateQueries({ queryKey: ['issues'] });
      } else setMsg({ ok: false, text: refusedWords(res.code) });
    } catch { setMsg({ ok: false, text: 'Not saved: Sweet Claims did not answer; try again.' }); }
  };
  if (msg?.ok) return <span className="v-msg ok" role="status">{msg.text}</span>;
  return (
    <>
      {/* one paid order (red, and its words say it pays: FACT_BUTTON's words, core factToneOf('police_order')) */}
      <PressButton label={FACT_BUTTON.police_order} className="btn pri sm" onPress={press} testId="v-order-report" tone={factToneOf('police_order')}
        title="One paid order to Clymer & Beverly for this crash, under the main client's case. The system checks again before it orders." />
      {msg ? <span className="v-msg bad" role="status">{msg.text}</span> : null}
    </>
  );
}

/** Approve's words on a suggested search: each search is billed, so it says it pays (ported 2026-10-25 with the medrec console's action tones:
 *  red, and never the colour alone, WCAG 1.4.1). The case card has room for the whole; the Queue's Action column holds the short form beside
 *  Not now, each pressed as a tight slot's press (the ring alone while it runs, press-button.tsx `ringOnly`), so the row keeps its 48 px. */
export const APPROVE_SEARCH_WORDS = Object.freeze({ full: 'Approve paid search', short: 'Approve (paid)' });
/** Approve / Not now on a suggested search. compact: the Queue's Action column (short words; the reason in the hover). */
export function Decide({ orderId, matterId, compact = false }: { orderId: string; matterId: string; compact?: boolean }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const send = async (d: CoastalDecision) => {
    setMsg(null);
    try {
      const res = await api.coastalDecide(orderId, d);
      if (res.result === 'saved') {
        setMsg({ ok: true, text: d === 'approve' ? 'Approved. The system orders the search within 15 minutes.' : 'Saved. The system asks again in 14 days.' });
        void qc.invalidateQueries({ queryKey: ['matter', matterId] }); void qc.invalidateQueries({ queryKey: ['coastal'] });
      } else setMsg({ ok: false, text: refusedWords(res.code) });
    } catch { setMsg({ ok: false, text: 'Not saved: Sweet Claims did not answer; try again.' }); }
  };
  // each button runs through usePress (press.ts): one press at a time, the other button waits while it runs; in the Queue's slot the ring
  // shows from the click and is held 300 ms (press.ts `ringNow`), as a tight slot's press
  const approve = usePress(() => send('approve'), { ringNow: compact });
  const notNow = usePress(() => send('not_now'), { ringNow: compact });
  const either = approve.busy || notNow.busy;
  if (msg?.ok) return <span className="v-msg ok" role="status" title={msg.text}>{compact ? busyWord(msg.text) : msg.text}</span>;
  return (
    <>
      {/* Approve orders a billed search (red, and its words say so); Not now leaves it (grey); core action_tone.ts */}
      <PressButton label={compact ? APPROVE_SEARCH_WORDS.short : APPROVE_SEARCH_WORDS.full} className="btn pri sm" busy={approve.busy} disabled={either} onPress={approve.run}
        testId="v-approve" ringOnly={compact} title="Order one Coastal Research policy-limits search for this case (each search is billed)" tone={actionToneOf('coastal', 'approve')} />
      <PressButton label="Not now" className="btn sm" busy={notNow.busy} disabled={either} onPress={notNow.run} testId="v-not-now" ringOnly={compact}
        title="Not now: the system asks again in 14 days" tone={actionToneOf('coastal', 'not_now')} />
      {msg ? <span className="v-msg bad" role="status" title={msg.text}>{compact ? 'Not saved' : msg.text}</span> : null}
    </>
  );
}
/** the Queue's short words for a saved decision */
const busyWord = (text: string) => (/^Approved/.test(text) ? 'Approved' : 'Not now: 14 days');

function Area({ name, view, action, testid }: { name: string; view: PoliceView | LimitsView; action: ReactNode; testid: string }) {
  return (
    <div className="v-row" data-testid={testid}>
      <span className="v-name">{name}</span>
      <span className="v-chip"><VendorChip chip={view.chip} tone={view.tone} /></span>
      <span className="v-line">{view.line}</span>
      <span className="v-act">{action}</span>
    </div>
  );
}

/** The card: drawn only when the case carries an area (the console's POLICE_ORDER or COASTAL on and its source readable). */
export function VendorAreas({ m }: { m: BoardMatter }) {
  const me = useMe().data;
  const v = m.vendors;
  const police = me?.policeArea === true ? v?.police ?? null : null;
  const limits = me?.coastal === true ? v?.limits ?? null : null;
  if (!police && !limits) return null;
  // the missing fact the team can type (2026-10-21): on the case's at-fault row that carries it (core board.ts policeFacts)
  const factRow = police?.factFields?.length ? m.rows.find((r) => r.side === 'DEF' && (r.policeFacts?.length ?? 0) > 0) ?? null : null;
  return (
    <section className="tcard vcard" aria-label="Police report and policy limits">
      <div className="tbar"><span style={{ fontWeight: 600, color: 'var(--ink)' }}>Police report and policy limits</span><span className="grow" />
        <span className="mt">ordered and searched by the claims system</span></div>
      {police ? <Area name="Police report" view={police} testid="v-police"
        action={police.canOrder && me?.policeOrder === true ? <OrderReport m={m} />
          : factRow && me?.policeOrder === true ? <InlineFact kind="police_fact" r={factRow} matterId={m.id} /> : null} /> : null}
      {limits ? <Area name="Policy limits" view={limits} testid="v-limits"
        action={limits.decide && me?.writeBack === true ? <Decide orderId={limits.decide.orderId} matterId={m.id} /> : null} /> : null}
    </section>
  );
}
